import * as React from 'react';
import { Modal, ModalFooter, ModalTitle, ModalBody, ModalHeader } from '@common/components/modal';
import { I18n } from 'react-redux-i18n';
import { Button } from '@common/components/Button';
import { FormControl } from '@common/components/form-control';
import { Flex } from '@common/components/Flex';

import { MODULE_NAME } from '../../audit-report';

import './data-export.styles.scss';

const DEFAULT_DELIMITER = undefined;
const NEWLINES_TYPES = {
  noFormatting: 0,
  asSpaces: 1,
  retainFormatted: 2
};

const EXPORT_TYPE = {
  txtTab: 'TXT_TAB',
  txtComma: 'TXT_COMMA',
  txtOther: 'TXT_OTHER',
  xls: 'XLS',
  xlsx: 'XLSX',
};

const RESTRICTED_NEWLINES = {
  [EXPORT_TYPE.txtComma]: [NEWLINES_TYPES.noFormatting] 
};

export const DataExport = (props) => {
  const { close, onExport } = props;

  const settings = React.useMemo(() => {
    return [
      { label: 'Text (Tab Delimited)', id: EXPORT_TYPE.txtTab },
      { label: 'Text (Comma Delimited)', id: EXPORT_TYPE.txtComma },
      { label: 'Text (Other)', id: EXPORT_TYPE.txtOther },
      { label: 'Excel (xls)', id: EXPORT_TYPE.xls },
      { label: 'Excel (xlsx)', id: EXPORT_TYPE.xlsx },
    ].map((x) => ({ ...x, label: I18n.t(x.label) }));
  }, []);

  const options = React.useMemo(() => {
    return [
      { label: 'Include Column Headings', metadata: { showHeaders: true } },
    ].map((x) => ({ ...x, id: x.label, label: I18n.t(x.label) }));
  }, []);

  const newlinesOptions = React.useMemo(() => {
    return [
      { label: 'Retain newlines in formatted cells', id: NEWLINES_TYPES.retainFormatted, },
      { label: 'Convert newlines to spaces', id: NEWLINES_TYPES.asSpaces, },
      { label: 'Export newlines without any formatting', id: NEWLINES_TYPES.noFormatting, },
    ].map((x) => ({ ...x, label: I18n.t(x.label) }));
  }, []);

  const [selectedSetting, setSelectedSetting] = React.useState(settings[0].id);
  const [selectedNewlinesSetting, setSelectedNewlinesSetting] = React.useState(newlinesOptions[0].id);
  const [selectedOptions, setSelectedOptions] = React.useState([]);
  const [delimiter, setDelimiter] = React.useState(DEFAULT_DELIMITER);
  const [submitting, setSubmitting] = React.useState(false);

  const handleNewlinesSettingsChange = React.useCallback((e) => {
    e.persist();

    const { value } = e.target;

    setSelectedNewlinesSetting(Number(value));
  }, []);

  const handleSettingsChange = React.useCallback((e) => {
    e.persist();

    const { value } = e.target;

    setSelectedSetting(value);
    setDelimiter(value === EXPORT_TYPE.txtOther ? '' : DEFAULT_DELIMITER);

    const restrictedNewlinesOptions = RESTRICTED_NEWLINES[value];
    const areRestrictedNewlinesOptionsSelected = restrictedNewlinesOptions?.includes(selectedNewlinesSetting);

    if (areRestrictedNewlinesOptionsSelected) {
      const defaultNewlinesOption = newlinesOptions.find((x) => !restrictedNewlinesOptions?.includes(x.id));

      setSelectedNewlinesSetting(defaultNewlinesOption.id);
    }
  }, [selectedSetting, selectedNewlinesSetting]);

  const handleOptionsChange = React.useCallback((e) => {
    e.persist();

    const { value } = e.target;

    const resultSettings = selectedOptions.includes(value)
      ? selectedOptions.filter((x) => x !== value)
      : [...selectedOptions, value];

    setSelectedOptions(resultSettings);
  }, [selectedOptions]);

  const handleDelimiterChange = React.useCallback((e) => {
    e.persist();

    const { value } = e.target;

    setDelimiter(value);
  }, []);

  const shouldIgnoreNewlines = [EXPORT_TYPE.xls, EXPORT_TYPE.xlsx].includes(selectedSetting);

  const handleExport = async () => {
    try {
      setSubmitting(true);

      const optionsSubmitData = selectedOptions.reduce((acc, id) => {
        const { metadata } = options.find((x) => x.id === id);

        return {
          ...acc,
          ...metadata
        };
      }, {});

      const defaultParams = {
        showHeaders: false
      };

      await onExport({
        ...defaultParams,
        delimiter,
        exportType: selectedSetting,
        newLinesOptions: shouldIgnoreNewlines ? undefined : selectedNewlinesSetting,
        ...optionsSubmitData,
      });
    } catch (err) {
      setSubmitting(false);
      throw err;
    }
  };

  return (
    <Modal
      show
      backdrop="static"
      bsClass="bootstrap modal"
      className="FieldAuditReportDataExport"
      animation={false}
      onEscapeKeyDown={close}
      onHide={close}
    >
      <ModalHeader closeButton onHide={close} className="FieldAuditReportDataExport__header">
        <ModalTitle id={`${MODULE_NAME}-export-data-header`}>
          {I18n.t('Export Data')}
        </ModalTitle>
      </ModalHeader>

      <ModalBody className="FieldAuditReportDataExport__content">
        <Flex alignItems="flex-start">
          <div className="FieldAuditReportDataExport__content__sections">
            <section className="FieldAuditReportDataExport__content__section">
              <span className="FieldAuditReportDataExport__content__section__title">{I18n.t('Settings')}</span>

              {settings.map((x) => (
                <Flex key={x.id} alignItems="center" justify="space-between">
                  <label className="FieldAuditReportDataExport__content__section__item">
                    <input 
                      id={`${MODULE_NAME}-export-setting-${x.id}`} 
                      type="radio" 
                      value={x.id}
                      checked={selectedSetting === x.id}
                      name={`${MODULE_NAME}-export-setting`} 
                      onChange={handleSettingsChange} 
                    />
                    {x.label}
                  </label>
                  {x.id === selectedSetting && x.id === EXPORT_TYPE.txtOther && (
                    <Flex className="FieldAuditReportDataExport__content__delimiter" alignItems="center">
                      <span>{I18n.t('Delimiter')}:&nbsp;</span>
                      <FormControl
                        value={delimiter}
                        onChange={handleDelimiterChange}
                      />
                    </Flex>
                  )}
                </Flex>
              ))}
            </section>

            <section className="FieldAuditReportDataExport__content__section">
              <span className="FieldAuditReportDataExport__content__section__title">{I18n.t('Options')}</span>

              {options.map((x) => (
                <label key={x.id} className="FieldAuditReportDataExport__content__section__item">
                  <input 
                    id={`${MODULE_NAME}-export-option-${x.id}`} 
                    type="checkbox" 
                    value={x.id}
                    checked={selectedOptions.includes(x.id)}
                    name={`${MODULE_NAME}-export-option`} 
                    onChange={handleOptionsChange} 
                  />
                  {x.label}
                </label>
              ))}
            </section>

            <section className="FieldAuditReportDataExport__content__section">
              <span className="FieldAuditReportDataExport__content__section__title">{I18n.t('Newline Handling for Text Fields')}</span>

              {newlinesOptions.map((x) => (
                <label key={x.id} className="FieldAuditReportDataExport__content__section__item">
                  <input 
                    disabled={shouldIgnoreNewlines || RESTRICTED_NEWLINES[selectedSetting]?.includes(x.id)}
                    id={`${MODULE_NAME}-export-newlines-setting-${x.id}`} 
                    type="radio" 
                    value={x.id}
                    checked={selectedNewlinesSetting === x.id}
                    name={`${MODULE_NAME}-export-newlines-setting`} 
                    onChange={handleNewlinesSettingsChange} 
                  />
                  {x.label}
                </label>
              ))}
            </section>
          </div>
          <Button 
            className="FieldAuditReportDataExport__content__submit-btn"
            disabled={submitting || (selectedSetting === EXPORT_TYPE.txtOther && !delimiter)}
            id={`${MODULE_NAME}-export-button`} 
            onClick={handleExport}
          >
            {submitting ? I18n.t('Exporting') : I18n.t('Export')}
          </Button>
        </Flex>
      </ModalBody>
    </Modal>
  );
};